import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import "../Pages/Gallery.css";
import { sweetConfirm } from "../utils/sweetAlerts";
import { RUNTIME_PUBLIC_BASE } from "../utils/runtimeBase";

const API = RUNTIME_PUBLIC_BASE || "";

const defaultOccasionForm = {
  id: null,
  title: "",
  description: "",
  thumbnail: null,
};

function GalleryManager() {
  const [occasions, setOccasions] = useState([]);
  const [activeOccasionId, setActiveOccasionId] = useState(null);
  const [occasionForm, setOccasionForm] = useState(defaultOccasionForm);
  const [occasionPreview, setOccasionPreview] = useState("");
  const [uploadState, setUploadState] = useState({});
  const [photoEdits, setPhotoEdits] = useState({});
  const [savingOccasion, setSavingOccasion] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const editingOccasion = useMemo(
    () => occasions.find((item) => item.id === occasionForm.id) || null,
    [occasionForm.id, occasions]
  );

  const activeOccasion = useMemo(
    () => occasions.find((item) => item.id === activeOccasionId) || null,
    [activeOccasionId, occasions]
  );

  const loadOccasions = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/gallery-occasions`);
      setOccasions(Array.isArray(response.data) ? response.data : []);
    } catch {
      setError("Failed to load gallery settings");
    }
  };

  useEffect(() => {
    loadOccasions();
  }, []);

  useEffect(() => {
    if (!occasionForm.thumbnail) return undefined;
    const nextUrl = URL.createObjectURL(occasionForm.thumbnail);
    setOccasionPreview(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [occasionForm.thumbnail]);

  const resetOccasionForm = () => {
    setOccasionForm(defaultOccasionForm);
    setOccasionPreview("");
  };

  const startEditOccasion = (occasion) => {
    setOccasionForm({
      id: occasion.id,
      title: occasion.title || "",
      description: occasion.description || "",
      thumbnail: null,
    });
    setOccasionPreview(occasion.thumbnail ? `${API}/uploads/${occasion.thumbnail}` : "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveOccasion = async () => {
    if (!occasionForm.title.trim()) {
      setError("Occasion title is required");
      return;
    }

    if (!occasionForm.id && !occasionForm.thumbnail) {
      setError("Occasion thumbnail is required");
      return;
    }

    try {
      setSavingOccasion(true);
      setError("");
      setMessage("");

      const formData = new FormData();
      formData.append("title", occasionForm.title.trim());
      formData.append("description", occasionForm.description.trim());
      if (occasionForm.thumbnail) {
        formData.append("thumbnail", occasionForm.thumbnail);
      }

      if (occasionForm.id) {
        await axios.post(`${API}/api/admin/gallery-occasions/${occasionForm.id}`, formData);
        setMessage("Main occasion updated successfully");
      } else {
        await axios.post(`${API}/api/admin/gallery-occasions`, formData);
        setMessage("Main occasion created successfully");
      }

      resetOccasionForm();
      loadOccasions();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Occasion save failed");
    } finally {
      setSavingOccasion(false);
    }
  };

  const deleteOccasion = async (id) => {
    if (!(await sweetConfirm("Delete this main occasion and all its images?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      await axios.delete(`${API}/api/admin/gallery-occasions/${id}`);
      setMessage("Main occasion deleted successfully");
      if (activeOccasionId === id) {
        setActiveOccasionId(null);
      }
      if (occasionForm.id === id) {
        resetOccasionForm();
      }
      loadOccasions();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Occasion delete failed");
    }
  };

  const setPhotoDraft = (occasionId, nextValue) => {
    setUploadState((prev) => ({
      ...prev,
      [occasionId]: {
        files: prev[occasionId]?.files || [],
        titles: prev[occasionId]?.titles || [],
        saving: prev[occasionId]?.saving || false,
        ...nextValue,
      },
    }));
  };

  const setPhotoEdit = (photoId, currentTitle, nextValue) => {
    setPhotoEdits((prev) => ({
      ...prev,
      [photoId]: {
        title: prev[photoId]?.title ?? currentTitle ?? "",
        image: prev[photoId]?.image ?? null,
        preview: prev[photoId]?.preview ?? "",
        saving: prev[photoId]?.saving ?? false,
        ...nextValue,
      },
    }));
  };

  const uploadPhotos = async (occasionId) => {
    const draft = uploadState[occasionId];
    const files = draft?.files || [];

    if (!files.length) {
      setError("Please select one or more images first");
      return;
    }

    try {
      setError("");
      setMessage("");
      setPhotoDraft(occasionId, { saving: true });

      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append("images[]", file);
        formData.append("title[]", draft?.titles?.[index] || "");
      });

      await axios.post(`${API}/api/admin/gallery-occasions/${occasionId}/photos`, formData);
      setMessage("Images added successfully");
      setPhotoDraft(occasionId, { files: [], titles: [], saving: false });
      loadOccasions();
    } catch (requestError) {
      setPhotoDraft(occasionId, { saving: false });
      setError(requestError.response?.data?.message || "Image upload failed");
    }
  };

  const savePhoto = async (photoId, currentTitle) => {
    const edit = photoEdits[photoId] || {};

    try {
      setError("");
      setMessage("");
      setPhotoEdit(photoId, currentTitle, { saving: true });

      const formData = new FormData();
      formData.append("title", edit.title ?? currentTitle ?? "");
      if (edit.image) {
        formData.append("image", edit.image);
      }

      await axios.post(`${API}/api/admin/gallery-photos/${photoId}`, formData);
      setMessage("Image updated successfully");
      setPhotoEdits((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
      loadOccasions();
    } catch (requestError) {
      setPhotoEdit(photoId, currentTitle, { saving: false });
      setError(requestError.response?.data?.message || "Image update failed");
    }
  };

  const deletePhoto = async (photoId) => {
    if (!(await sweetConfirm("Delete this image?", { confirmButtonText: "Yes, Delete" }))) return;

    try {
      await axios.delete(`${API}/api/admin/gallery-photos/${photoId}`);
      setMessage("Image deleted successfully");
      loadOccasions();
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Image delete failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="container-fluid" style={{ display: "grid", gap: "22px" }}>
        <div>
          <h3 style={{ marginBottom: "6px" }}>Gallery Setting</h3>
          <p className="text-muted mb-0">
            Front-end er moto same view maintain kora hoyeche. Upore main occasion upload korun, niche list theke occasion open kore tar vitore image manage korun.
          </p>
        </div>

        {message ? <div className="alert alert-success mb-0">{message}</div> : null}
        {error ? <div className="alert alert-danger mb-0">{error}</div> : null}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">{editingOccasion ? "Edit Main Occasion" : "Upload Main Occasion"}</h5>
                <div className="text-muted">Main thumbnail, title and description add korun. Eta front-end e main occasion card hisebe show hobe.</div>
              </div>
              {editingOccasion ? (
                <button type="button" className="btn btn-outline-secondary" onClick={resetOccasionForm}>
                  Cancel Edit
                </button>
              ) : null}
            </div>

            <div className="row g-3 align-items-start">
              <div className="col-xl-3 col-lg-4">
                <label className="form-label fw-semibold">Main Occasion Title</label>
                <input
                  className="form-control"
                  value={occasionForm.title}
                  onChange={(event) => setOccasionForm((prev) => ({ ...prev, title: event.target.value }))}
                />
              </div>
              <div className="col-xl-3 col-lg-4">
                <label className="form-label fw-semibold">Main Thumbnail</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={(event) => setOccasionForm((prev) => ({ ...prev, thumbnail: event.target.files?.[0] || null }))}
                />
              </div>
              <div className="col-xl-3 col-lg-4">
                <label className="form-label fw-semibold">Thumbnail Preview</label>
                <div className="border rounded-4 bg-light p-2">
                  {occasionPreview ? (
                    <img src={occasionPreview} alt="Occasion preview" style={{ width: "100%", height: "120px", objectFit: "cover", borderRadius: "12px" }} />
                  ) : (
                    <div className="text-muted d-flex align-items-center justify-content-center" style={{ height: "120px" }}>No thumbnail selected</div>
                  )}
                </div>
              </div>
              <div className="col-xl-3 col-lg-12">
                <label className="form-label fw-semibold">Main Occasion Description</label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={occasionForm.description}
                  onChange={(event) => setOccasionForm((prev) => ({ ...prev, description: event.target.value }))}
                />
              </div>
              <div className="col-12 d-flex justify-content-end">
                <button type="button" className="btn btn-primary px-4" onClick={saveOccasion} disabled={savingOccasion}>
                  {savingOccasion ? "Saving..." : editingOccasion ? "Update Main Occasion" : "Upload Main Occasion"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {!activeOccasion ? (
          <>
            <div className="gallery-occasion-list">
              {occasions.map((occasion) => (
                <button
                  key={occasion.id}
                  type="button"
                  className="gallery-occasion-card"
                  onClick={() => setActiveOccasionId(occasion.id)}
                >
                  <div className="gallery-occasion-thumb">
                    <img src={`${API}/uploads/${occasion.thumbnail}`} alt={occasion.title} />
                  </div>
                  <div className="gallery-occasion-copy">
                    <h3>{occasion.title}</h3>
                  </div>
                </button>
              ))}
            </div>

            {!occasions.length ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-muted">No main occasion created yet.</div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="gallery-album-panel">
            <div className="gallery-album-heading">
              <div>
                <h2>{activeOccasion.title}</h2>
                <p>{activeOccasion.description || "No description available for this occasion yet."}</p>
              </div>
              <div className="gallery-album-actions">
                <div className="gallery-album-count">{activeOccasion.photos?.length || 0} images</div>
                <button type="button" className="gallery-back-button" onClick={() => setActiveOccasionId(null)}>
                  Back To Occasion List
                </button>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Main Occasion Control</h5>
                    <div className="text-muted">Front-end view er sathe same gallery open ache. Ekhane extra admin action paben.</div>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => startEditOccasion(activeOccasion)}>
                      Edit Thumbnail
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deleteOccasion(activeOccasion.id)}>
                      Delete Occasion
                    </button>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Add Images</label>
                    <input
                      type="file"
                      className="form-control"
                      multiple
                      onChange={(event) => {
                        const files = Array.from(event.target.files || []);
                        const draft = uploadState[activeOccasion.id] || { titles: [] };
                        setPhotoDraft(activeOccasion.id, {
                          files,
                          titles: files.map((_, index) => draft.titles?.[index] || ""),
                        });
                      }}
                    />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Image Titles</label>
                    <div className="d-grid gap-2" style={{ maxHeight: "160px", overflowY: "auto" }}>
                      {(uploadState[activeOccasion.id]?.files || []).length ? (
                        (uploadState[activeOccasion.id]?.files || []).map((file, index) => (
                          <input
                            key={`${file.name}-${index}`}
                            className="form-control form-control-sm"
                            placeholder={`Title for ${file.name}`}
                            value={uploadState[activeOccasion.id]?.titles?.[index] || ""}
                            onChange={(event) => {
                              const nextTitles = [...(uploadState[activeOccasion.id]?.titles || [])];
                              nextTitles[index] = event.target.value;
                              setPhotoDraft(activeOccasion.id, { titles: nextTitles });
                            }}
                          />
                        ))
                      ) : (
                        <div className="text-muted small">No image selected yet.</div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-2 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={() => uploadPhotos(activeOccasion.id)}
                      disabled={uploadState[activeOccasion.id]?.saving}
                    >
                      {uploadState[activeOccasion.id]?.saving ? "Uploading..." : "Add Image"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="gallery-grid">
              {(activeOccasion.photos || []).map((photo) => {
                const edit = photoEdits[photo.id];

                return (
                  <div key={photo.id} className="gallery-item" style={{ background: "#ffffff" }}>
                    <img src={edit?.preview || `${API}/uploads/${photo.image}`} alt={photo.title || activeOccasion.title} />
                    <div className="p-3 d-grid gap-2" style={{ background: "#ffffff" }}>
                      <input
                        className="form-control form-control-sm"
                        value={edit?.title ?? photo.title ?? ""}
                        placeholder="Image title"
                        onChange={(event) => setPhotoEdit(photo.id, photo.title || "", { title: event.target.value })}
                      />
                      <input
                        type="file"
                        className="form-control form-control-sm"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          const preview = file ? URL.createObjectURL(file) : "";
                          setPhotoEdit(photo.id, photo.title || "", { image: file, preview });
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => savePhoto(photo.id, photo.title || "")}
                        disabled={edit?.saving}
                      >
                        {edit?.saving ? "Saving..." : "Save Image"}
                      </button>
                      <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => deletePhoto(photo.id)}>
                        Delete Image
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {!activeOccasion.photos?.length ? (
              <div className="gallery-empty mt-4">No image uploaded in this occasion yet.</div>
            ) : null}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default GalleryManager;



